/**
 * URL parse/serialize for VehicleSearchParams (Milestone 4).
 */
import {
  clampPage,
  clampPageSize,
  createDefaultVehicleSearchParams,
  normalizeBodyTypeFilter,
  normalizeCanonicalSort,
  normalizeTransmissionFilter,
  parseCsvParam,
  resolveCityLabel,
  VEHICLE_SEARCH_SORT,
} from "../../constants/vehicleSearchParamsConstants";
import { parseSearchViewModeFromParams, serializeSearchViewModeParam } from "./searchViewMode";
import { normalizeFuelTypeFilter, normalizeFuelTypesFilter } from "./vehicleSearchFuelType";

function pickNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {URLSearchParams|string|object} input
 */
export function parseVehicleSearchParams(input) {
  const defaults = createDefaultVehicleSearchParams();

  if (input && typeof input === "object" && !(input instanceof URLSearchParams)) {
    const raw = input;
    return {
      ...defaults,
      keyword: (raw.keyword ?? raw.search)?.trim() || null,
      city: resolveCityLabel(raw.city),
      department: raw.department?.trim() || null,
      make: raw.make?.trim() || null,
      model: raw.model?.trim() || null,
      yearMin: pickNumber(raw.yearMin ?? raw.year_min) || null,
      yearMax: pickNumber(raw.yearMax ?? raw.year_max) || null,
      priceMinCop: pickNumber(raw.priceMinCop ?? raw.price_min ?? raw.minPrice) || null,
      priceMaxCop: pickNumber(raw.priceMaxCop ?? raw.price_max ?? raw.maxPrice) || null,
      mileageMinKm: pickNumber(raw.mileageMinKm ?? raw.mileage_min) || null,
      mileageMaxKm: pickNumber(raw.mileageMaxKm ?? raw.mileage_max) || null,
      transmission: normalizeTransmissionFilter(raw.transmission),
      fuelType: normalizeFuelTypeFilter(raw.fuelType ?? raw.fuel_type),
      fuelTypes: normalizeFuelTypesFilter(
        Array.isArray(raw.fuelTypes)
          ? raw.fuelTypes
          : parseCsvParam(raw.fuelTypes || raw.fuel_types)
      ),
      bodyType: normalizeBodyTypeFilter(raw.bodyType ?? raw.body_type ?? raw.body),
      dealerVerified: Boolean(raw.dealerVerified || raw.verified_only || raw.verifiedOnly),
      fairPriceLabels: Array.isArray(raw.fairPriceLabels)
        ? raw.fairPriceLabels
        : parseCsvParam(raw.fairPriceLabels || raw.fair_price),
      riskLevels: Array.isArray(raw.riskLevels) ? raw.riskLevels : parseCsvParam(raw.riskLevels || raw.risk),
      hideHighRisk: Boolean(raw.hideHighRisk || raw.hide_risk_alert || raw.hideRiskAlert),
      recentlyConfirmedOnly: Boolean(
        raw.recentlyConfirmedOnly || raw.availability_confirmed || raw.availabilityConfirmed
      ),
      hasPhotos: Boolean(raw.hasPhotos || raw.has_photos),
      requirePhotos: Boolean(raw.requirePhotos || raw.require_photos),
      metroArea: raw.metroArea?.trim() || raw.metro_area?.trim() || null,
      availabilityStatus: raw.availabilityStatus?.trim() || raw.availability_status?.trim() || null,
      completeInfoOnly: Boolean(raw.completeInfoOnly || raw.complete_info_only),
      sort: normalizeCanonicalSort(raw.sort),
      page: clampPage(raw.page),
      pageSize: clampPageSize(raw.pageSize),
      view: parseSearchViewModeFromParams(
        raw.view != null ? new URLSearchParams({ view: String(raw.view) }) : ""
      ),
    };
  }

  const sp =
    input instanceof URLSearchParams
      ? input
      : new URLSearchParams(typeof input === "string" ? input.replace(/^\?/, "") : "");

  const params = { ...defaults };
  const keyword = sp.get("keyword") || sp.get("search");
  if (keyword?.trim()) params.keyword = keyword.trim();

  params.city = resolveCityLabel(sp.get("city"));
  const department = sp.get("department");
  if (department?.trim()) params.department = department.trim();

  const make = sp.get("make");
  if (make?.trim()) params.make = make.trim();

  const model = sp.get("model");
  if (model?.trim()) params.model = model.trim();

  params.yearMin = pickNumber(sp.get("yearMin") || sp.get("year_min"));
  params.yearMax = pickNumber(sp.get("yearMax") || sp.get("year_max"));

  const priceMin = pickNumber(sp.get("priceMinCop") || sp.get("price_min"));
  const priceMax = pickNumber(sp.get("priceMaxCop") || sp.get("price_max"));
  params.priceMinCop = priceMin && priceMin > 0 ? priceMin : null;
  params.priceMaxCop = priceMax && priceMax > 0 && priceMax < 999_999_999 ? priceMax : null;

  params.mileageMinKm = pickNumber(sp.get("mileageMinKm") || sp.get("mileage_min"));
  params.mileageMaxKm = pickNumber(sp.get("mileageMaxKm") || sp.get("mileage_max"));

  params.transmission = normalizeTransmissionFilter(sp.get("transmission"));
  const fuelType = sp.get("fuelType") || sp.get("fuel_type");
  if (fuelType?.trim()) params.fuelType = normalizeFuelTypeFilter(fuelType.trim());
  params.fuelTypes = normalizeFuelTypesFilter(
    parseCsvParam(sp.get("fuelTypes") || sp.get("fuel_types"))
  );
  if (!params.fuelTypes.length && params.fuelType) {
    params.fuelTypes = [params.fuelType];
    params.fuelType = null;
  }

  params.bodyType = normalizeBodyTypeFilter(
    sp.get("bodyType") || sp.get("body_type") || sp.get("body")
  );

  params.dealerVerified =
    sp.get("dealerVerified") === "true" || sp.get("verified_only") === "true";

  const fairCsv = sp.get("fairPriceLabels") || sp.get("fair_price");
  params.fairPriceLabels = parseCsvParam(fairCsv);
  if (!params.fairPriceLabels.length && sp.get("below_fair_value_only") === "true") {
    params.fairPriceLabels = ["below_market", "fair_price"];
  }

  params.riskLevels = parseCsvParam(sp.get("riskLevels") || sp.get("risk"));
  params.hideHighRisk = sp.get("hideHighRisk") === "true" || sp.get("hide_risk_alert") === "true";

  params.recentlyConfirmedOnly =
    sp.get("recentlyConfirmedOnly") === "true" || sp.get("availability_confirmed") === "true";

  params.hasPhotos = sp.get("hasPhotos") === "true" || sp.get("has_photos") === "true";
  params.requirePhotos = sp.get("requirePhotos") === "true" || sp.get("require_photos") === "true";
  const metroArea = sp.get("metroArea") || sp.get("metro_area");
  if (metroArea?.trim()) params.metroArea = metroArea.trim();

  const availability = sp.get("availabilityStatus") || sp.get("availability_status");
  if (availability?.trim()) params.availabilityStatus = availability.trim();

  params.completeInfoOnly = sp.get("completeInfoOnly") === "true" || sp.get("complete_info_only") === "true";

  params.sort = normalizeCanonicalSort(sp.get("sort"));
  params.page = clampPage(sp.get("page"));
  params.pageSize = clampPageSize(sp.get("pageSize"));
  params.view = parseSearchViewModeFromParams(sp);

  return params;
}

export function serializeVehicleSearchParams(params) {
  const sp = new URLSearchParams();
  const p = parseVehicleSearchParams(params);
  const defaults = createDefaultVehicleSearchParams();

  if (p.keyword) sp.set("keyword", p.keyword);
  if (p.city) sp.set("city", p.city);
  if (p.department) sp.set("department", p.department);
  if (p.make) sp.set("make", p.make);
  if (p.model) sp.set("model", p.model);
  if (p.yearMin != null && p.yearMin > 0) sp.set("yearMin", String(p.yearMin));
  if (p.yearMax != null && p.yearMax > 0) sp.set("yearMax", String(p.yearMax));
  if (p.priceMinCop != null && p.priceMinCop > 0) sp.set("priceMinCop", String(p.priceMinCop));
  if (p.priceMaxCop != null && p.priceMaxCop > 0) sp.set("priceMaxCop", String(p.priceMaxCop));
  if (p.mileageMinKm != null && p.mileageMinKm > 0) sp.set("mileageMinKm", String(p.mileageMinKm));
  if (p.mileageMaxKm != null && p.mileageMaxKm > 0) sp.set("mileageMaxKm", String(p.mileageMaxKm));
  if (p.transmission) sp.set("transmission", p.transmission);
  if (p.fuelTypes?.length) sp.set("fuelTypes", p.fuelTypes.join(","));
  else if (p.fuelType) sp.set("fuelType", p.fuelType);
  if (p.bodyType) sp.set("bodyType", p.bodyType);
  if (p.dealerVerified) sp.set("dealerVerified", "true");
  if (p.fairPriceLabels?.length) sp.set("fairPriceLabels", p.fairPriceLabels.join(","));
  if (p.riskLevels?.length) sp.set("riskLevels", p.riskLevels.join(","));
  if (p.hideHighRisk) sp.set("hideHighRisk", "true");
  if (p.recentlyConfirmedOnly) sp.set("recentlyConfirmedOnly", "true");
  if (p.hasPhotos) sp.set("hasPhotos", "true");
  if (p.requirePhotos) sp.set("requirePhotos", "true");
  if (p.metroArea) sp.set("metroArea", p.metroArea);
  if (p.availabilityStatus) sp.set("availabilityStatus", p.availabilityStatus);
  if (p.completeInfoOnly) sp.set("completeInfoOnly", "true");
  if (p.sort && p.sort !== defaults.sort) sp.set("sort", p.sort);
  if (p.page > 1) sp.set("page", String(p.page));
  if (p.pageSize !== defaults.pageSize) sp.set("pageSize", String(p.pageSize));
  const viewParam = serializeSearchViewModeParam(p.view);
  if (viewParam) sp.set("view", viewParam);

  return sp;
}

export function updateVehicleSearchUrl(params, options = {}) {
  const cleared = clearVehicleSearchFilters(params, options);
  const qs = serializeVehicleSearchParams(cleared).toString();
  const pathname =
    options.pathname ||
    (typeof window !== "undefined" ? window.location.pathname : "/v/automap/vehicles");
  return qs ? `${pathname}?${qs}` : pathname;
}

export function clearVehicleSearchFilters(current = {}, options = {}) {
  const defaults = createDefaultVehicleSearchParams();
  const preserveKeys = options.preserveKeys || [];
  const next = { ...defaults };
  preserveKeys.forEach((key) => {
    const value = current[key];
    if (value != null && value !== "" && value !== false && !(Array.isArray(value) && !value.length)) {
      next[key] = value;
    }
  });
  return next;
}

export function vehicleSearchParamsFromSeoConfig(config) {
  const filters = config?.inventoryFilters || {};
  const params = createDefaultVehicleSearchParams();
  if (filters.city) params.city = resolveCityLabel(filters.city);
  if (filters.make) params.make = filters.make;
  if (filters.model) params.model = filters.model;
  if (filters.body_type) params.bodyType = normalizeBodyTypeFilter(filters.body_type);
  if (filters.transmission) params.transmission = normalizeTransmissionFilter(filters.transmission);
  if (filters.price_min != null) params.priceMinCop = Number(filters.price_min);
  if (filters.price_max != null) params.priceMaxCop = Number(filters.price_max);
  return params;
}

export function resetModelForMakeChange(params, make, modelsForMake = []) {
  if (!params.model) return params;
  if (!make) return { ...params, model: null };
  const belongs = modelsForMake.some(
    (m) => String(m).toLowerCase() === String(params.model).toLowerCase()
  );
  return belongs ? params : { ...params, model: null };
}

export function isDefaultSort(sort) {
  return !sort || sort === VEHICLE_SEARCH_SORT.RECOMMENDED;
}
